import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import { bibleService } from '../../../services/bibleService';

// Fix mojibake encoding (UTF-8 bytes stored as Latin-1)
const fixEncoding = (str) => {
  if (!str) return str;
  try {
    const bytes = new Uint8Array([...str].map(c => c.charCodeAt(0)));
    return new TextDecoder('utf-8')?.decode(bytes);
  } catch {
    return str;
  }
};

// ── Option A: Version Pairing Table ──────────────────────────────────────────
// Maps base version short_name (lowercase) → paired Strongs version short_name (lowercase)
// The Strongs version is hidden from the selector; it's accessed via the Strong's toggle.
export const VERSION_PAIRS = {
  'kjv': 'kjv strongs',
  'rv1909': 'rv1909 s',
  'rv 1909': 'rv1909 s',
  // ASV + ASV Strong's pairing'asv': 'asv strongs',
  // Chinese Union (Simp) pairing
  'chinese union (simp)': 'chinese union (simp) strongs',
};

// Additional Strong's-only short names that should always be hidden from the selector
// even if they don't match a VERSION_PAIRS value exactly (e.g. due to import naming)
const STRONGS_ONLY_PATTERNS = [
  /^asv.?strong/i,
  /^chinese.?union.?\(simp\).?strong/i,
  /^cuv.?strong/i,
  /^cunp.?strong/i,
];

// Given a version short_name (lowercase), return the paired Strongs version short_name (or null)
export const getStrongsPairFor = (shortNameLower) => {
  return VERSION_PAIRS?.[shortNameLower] || null;
};

// Dynamic lookup: given a base version short_name and the full list of available version short_names,
// find the actual Strong's companion version name in the DB (handles unknown import naming).
export const findStrongsCompanion = (baseShortNameLower, allShortNamesLower) => {
  // 1. Try exact VERSION_PAIRS match first
  const exact = VERSION_PAIRS?.[baseShortNameLower];
  if (exact && allShortNamesLower?.includes(exact)) return exact;

  // 2. Pattern-based search: find a version that matches a Strong's pattern
  //    AND starts with the same prefix as the base version
  const basePrefix = baseShortNameLower?.split(/[\s+]/)?.[0]; // e.g. 'asv', 'cuv', 'chinese'
  return allShortNamesLower?.find(n =>
    STRONGS_ONLY_PATTERNS?.some(p => p?.test(n)) && n?.startsWith(basePrefix)
  ) || null;
};

// Returns true if this version is a Strongs-only paired version (should be hidden from selector)
const isStrongsOnlyVersion = (shortNameLower) => {
  const pairedValues = Object.values(VERSION_PAIRS);
  if (pairedValues?.includes(shortNameLower)) return true;
  // Also hide versions whose short name matches known Strong's-only patterns
  return STRONGS_ONLY_PATTERNS?.some(pattern => pattern?.test(shortNameLower));
};

const BibleVersionSelector = ({
  selectedVersion,
  onVersionChange,
  onVersionDataChange,
  className = ''
}) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // The version highlighted/previewed inside the modal (not yet confirmed)
  const [previewVersion, setPreviewVersion] = useState(null);

  useEffect(() => {
    const loadBibleVersions = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data, error } = await bibleService?.getBibleVersions();
        if (error) { setError('Failed to load Bible versions'); return; }
        if (!data || data?.length === 0) { setError('No Bible versions available'); return; }

        const allShortNamesLower = data?.map(v => v?.short_name?.toLowerCase()?.trim());

        // Check if any Strong's companion exists for a given base version short name
        const hasStrongsCompanion = (shortLower) => {
          // Check exact VERSION_PAIRS match
          const pairedName = VERSION_PAIRS?.[shortLower];
          if (pairedName && allShortNamesLower?.some(n => n === pairedName)) return true;
          // Check pattern-based match (handles unknown import naming)
          return allShortNamesLower?.some(n => STRONGS_ONLY_PATTERNS?.some(p => p?.test(n)) && n?.startsWith(shortLower?.split(' ')?.[0]));
        };

        const versionOptions = data?.map(version => {
          const shortLower = version?.short_name?.toLowerCase()?.trim();

          return {
            value: shortLower,
            label: version?.short_name || version?.version_name,
            fullLabel: `${version?.version_name} (${version?.short_name})`,
            language: version?.language || 'English',
            description: fixEncoding(version?.description),
            hasStrongs: version?.has_strongs === true || hasStrongsCompanion(shortLower),
            isStrongsOnly: isStrongsOnlyVersion(shortLower),
          };
        });

        setVersions(versionOptions);
      } catch (err) {
        setError('Failed to load Bible versions');
      } finally {
        setLoading(false);
      }
    };
    loadBibleVersions();
  }, []);

  // Notify parent when selected version data changes
  useEffect(() => {
    if (!versions?.length) return;
    const vd = versions?.find(v =>
      v?.value === selectedVersion?.toLowerCase?.()?.trim() ||
      v?.label?.toLowerCase()?.trim() === selectedVersion?.toLowerCase?.()?.trim()
    );
    if (vd && onVersionDataChange) {
      onVersionDataChange({ language: vd?.language, hasStrongs: vd?.hasStrongs });
    }
  }, [versions, selectedVersion]);

  const selectedVersionData = (() => {
    if (loading) return null;
    return versions?.find(v =>
      v?.value === selectedVersion?.toLowerCase?.()?.trim() ||
      v?.label?.toLowerCase()?.trim() === selectedVersion?.toLowerCase?.()?.trim()
    );
  })();

  const selectedLabel = selectedVersionData?.label || (loading ? '...' : selectedVersion || 'Ver');

  const visibleVersions = versions?.filter(v => !v?.isStrongsOnly);

  const groupedVersions = (() => {
    const groups = {};
    visibleVersions?.forEach(v => {
      const lang = v?.language || 'English';
      if (!groups?.[lang]) groups[lang] = [];
      groups?.[lang]?.push(v);
    });
    const sorted = Object.keys(groups)?.sort((a, b) => {
      if (a?.toLowerCase() === 'english') return -1;
      if (b?.toLowerCase() === 'english') return 1;
      return a?.localeCompare(b);
    });
    return sorted?.map(lang => ({ language: lang, versions: groups?.[lang] }));
  })();

  const isSelected = (v) =>
    v?.value === selectedVersion?.toLowerCase?.()?.trim() ||
    v?.label?.toLowerCase()?.trim() === selectedVersion?.toLowerCase?.()?.trim();

  const openModal = () => {
    setPreviewVersion(selectedVersionData || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setPreviewVersion(null);
  };

  const handleVersionClick = (version) => {
    setPreviewVersion(version);
  };

  const handleConfirm = () => {
    if (previewVersion) {
      onVersionChange?.(previewVersion?.value);
      if (onVersionDataChange) {
        onVersionDataChange({ language: previewVersion?.language, hasStrongs: previewVersion?.hasStrongs });
      }
    }
    closeModal();
  };

  if (loading) {
    return <div className={`${className}`}><div className="h-8 w-14 bg-muted rounded animate-pulse"></div></div>;
  }

  if (error) {
    return <div className={`${className}`}><div className="text-xs text-destructive">{error}</div></div>;
  }

  return (
    <div className={`flex items-center ${className}`}>
      {/* Trigger button — same height as book/chapter buttons */}
      <button
        onClick={openModal}
        className="flex items-center gap-1 px-2 py-1 h-8 text-xs font-medium rounded border border-border bg-background hover:bg-muted transition-colors flex-shrink-0 min-w-[44px]"
        title="Select Bible version"
      >
        <span className="truncate">{selectedLabel}</span>
        <Icon name="ChevronDown" size={12} className="flex-shrink-0" />
      </button>
      {/* Version Selector Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) closeModal(); }}
        >
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-2">
                <Icon name="BookOpen" size={18} className="text-primary" />
                <h2 className="text-base font-semibold text-foreground">Select Bible Version</h2>
              </div>
              <button
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            {/* Body: version list + info panel */}
            <div className="flex flex-1 min-h-0">
              {/* Version list */}
              <div className="w-1/2 border-r border-border overflow-y-auto">
                {groupedVersions?.map((group) => (
                  <div key={group?.language}>
                    <div className="px-3 pt-3 pb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest bg-muted/30 sticky top-0">
                      {group?.language}
                    </div>
                    {group?.versions?.map((version) => {
                      const active = isSelected(version);
                      const previewing = previewVersion?.value === version?.value;
                      return (
                        <button
                          key={version?.value}
                          onClick={() => handleVersionClick(version)}
                          className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-2 ${
                            previewing
                              ? 'bg-primary/15 text-primary font-semibold'
                              : active
                              ? 'bg-primary/8 text-primary font-medium' :'text-foreground hover:bg-muted'
                          }`}
                        >
                          <span className="flex-1 truncate">{version?.label}</span>
                          {version?.hasStrongs && !version?.isStrongsOnly && (
                            <span className="text-[9px] font-bold uppercase tracking-wide text-amber-500 bg-amber-500/10 px-1 py-0.5 rounded flex-shrink-0">
                              S
                            </span>
                          )}
                          {active && (
                            <Icon name="Check" size={13} className="text-primary flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Info panel */}
              <div className="w-1/2 p-4 overflow-y-auto flex flex-col">
                {previewVersion ? (
                  <>
                    <div className="text-sm font-semibold text-foreground mb-1 leading-snug">
                      {previewVersion?.fullLabel}
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      Language: <span className="text-foreground">{previewVersion?.language}</span>
                    </div>
                    {previewVersion?.hasStrongs && (
                      <div className="flex items-center gap-1.5 text-xs text-amber-500 mb-3 bg-amber-500/10 px-2 py-1.5 rounded-md">
                        <Icon name="Star" size={12} />
                        <span>Strong's Numbers available</span>
                      </div>
                    )}
                    {previewVersion?.description ? (
                      <p className="text-xs text-muted-foreground leading-relaxed flex-1">
                        {previewVersion?.description}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">No description available.</p>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center gap-2 text-muted-foreground">
                    <Icon name="MousePointerClick" size={24} className="opacity-40" />
                    <p className="text-xs">Select a version to see details</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border flex-shrink-0">
              <button
                onClick={closeModal}
                className="px-4 py-1.5 text-sm rounded-md border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={!previewVersion}
                className="px-4 py-1.5 text-sm rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Select Version
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BibleVersionSelector;